import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Badge,
  Center,
  Group,
  Loader,
  Pagination,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

import { EditableCodename } from '@/components/EditableCodename';
import { BROWSE_PAGE_SIZE, BrowseEntityKind, BrowseEntityRow } from '@/hooks/useBrowseEntities';
import { useSearchDraft } from '@/hooks/useSearchDraft';
import { useTeams } from '@/hooks/useTeams';
import { timeAgo } from './timeAgo';

export type BrowseMode = 'cases' | 'all';

interface BrowseTabProps {
  /** ONE server page: flat rows (`all`) or every member of the page's
   * families (`cases`) — already searched and paginated server-side. */
  rows: BrowseEntityRow[];
  /** Rows (`all`) or families (`cases`) across all pages — drives the pager. */
  total: number;
  page: number;
  onPageChange: (next: number) => void;
  isLoading: boolean;
  error: Error | null;
  /** Committed (URL-persisted) search value; seeds the local draft. */
  search: string;
  /** Fired once per settled (debounced) draft — the URL write, which the
   * page turns into the server `q` (and resets the page). */
  onSearchChange: (next: string) => void;
  /** Cases vs All-items — URL-persisted by the page so reloads and
   * shared links keep the view. */
  mode: BrowseMode;
  onModeChange: (next: BrowseMode) => void;
  /** Fired after a codename rename (or stale-key race) — refetch. */
  onCodenameRenamed?: () => void | Promise<void>;
}

function pillForKind(kind: BrowseEntityKind): { label: string; color: string } {
  switch (kind) {
    case 'enquiry':
      return { label: 'Enquiry', color: 'gray' };
    case 'report_request':
      return { label: 'Request', color: 'blue' };
    case 'report':
      return { label: 'Report', color: 'teal' };
  }
}

function routeForRow(row: BrowseEntityRow): string {
  // Enquiries and requests land on the CASE page with the node
  // pre-selected; rows without a family (legacy) fall back to the
  // standalone routes, which resolve-and-redirect themselves.
  const family = row.codenameFamily ? encodeURIComponent(row.codenameFamily) : null;
  switch (row.kind) {
    case 'enquiry':
      return family ? `/cases/${family}?item=enq:${row.id}` : `/admin/enquiries/${row.id}`;
    case 'report_request':
      return family ? `/cases/${family}?item=rr:${row.id}` : `/admin/triage/${row.id}`;
    case 'report':
      return `/reports/${row.id}`;
  }
}

interface CaseGroup {
  /** Family key, or a per-row fallback for family-less legacy rows. */
  key: string;
  family: string | null;
  members: BrowseEntityRow[];
  teamId: string | null;
  latest: string | null;
}

const KIND_ORDER: Record<BrowseEntityKind, number> = {
  enquiry: 0,
  report_request: 1,
  report: 2,
};

/** Group the flat entity rows into case (codename-family) rows. */
function groupIntoCases(rows: BrowseEntityRow[]): CaseGroup[] {
  const byKey = new Map<string, CaseGroup>();
  for (const row of rows) {
    // A family-less row (pre-backfill legacy only) gets its own
    // single-member case rather than an "(uncoded)" bucket.
    const key = row.codenameFamily ?? `${row.kind}:${row.id}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, family: row.codenameFamily, members: [], teamId: null, latest: null };
      byKey.set(key, group);
    }
    group.members.push(row);
    group.teamId = group.teamId ?? row.teamId;
    if (row.createdAt && (!group.latest || row.createdAt > group.latest)) {
      group.latest = row.createdAt;
    }
  }
  const groups = [...byKey.values()];
  for (const group of groups) {
    group.members.sort(
      (a, b) =>
        KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
        (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
    );
  }
  groups.sort((a, b) => (b.latest ?? '').localeCompare(a.latest ?? ''));
  return groups;
}

/**
 * Navigation surface over every Enquiry / ReportRequest / Report the
 * analyst can reach. Two modes:
 *
 * - **Cases** (default) — one row per codename family: the case's
 *   editable codename plus its members as clickable stage chips.
 * - **All items** — the flat entity table.
 *
 * Search, the team filter and pagination are server-side
 * (`GET /inbox/browse`): the settled search draft is committed to the
 * URL through `useSearchDraft`, and the page turns it into the server
 * `q`. In Cases mode the server pages families and hands back every
 * member of the page's families, so grouping here yields whole cases.
 * No inline mutations beyond codename rename — Browse is navigation.
 */
export function BrowseTab({
  rows,
  total,
  page,
  onPageChange,
  isLoading,
  error,
  search,
  onSearchChange,
  mode,
  onModeChange,
  onCodenameRenamed,
}: Readonly<BrowseTabProps>) {
  const navigate = useNavigate();
  const [searchDraft, setSearchDraft] = useSearchDraft(search, onSearchChange);

  const cases = useMemo(() => groupIntoCases(rows), [rows]);
  const totalPages = Math.max(1, Math.ceil(total / BROWSE_PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);

  // Human-readable client-team names (raw team ids mean nothing to an
  // analyst). Best-effort: unknown ids fall back to the id itself.
  const { teams } = useTeams();
  const teamName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of teams ?? []) {
      map.set(String(t.id), t.name);
    }
    return (teamId: string | null) => (teamId ? (map.get(teamId) ?? teamId) : null);
  }, [teams]);

  return (
    <Stack gap="md" data-testid="browse-tab">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <TextInput
            placeholder="Search by codename…"
            leftSection={<IconSearch size={14} />}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.currentTarget.value)}
            w={320}
            data-testid="browse-search"
          />
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={(v) => onModeChange(v as BrowseMode)}
            data={[
              { value: 'cases', label: 'Cases' },
              { value: 'all', label: 'All items' },
            ]}
            data-testid="browse-mode"
          />
        </Group>
        <Text size="sm" c="dimmed">
          {mode === 'cases'
            ? `${total} case${total === 1 ? '' : 's'}`
            : `${total} matching row${total === 1 ? '' : 's'}`}
        </Text>
      </Group>

      {error && (
        <Text c="red" size="sm">
          Failed to load browse data: {error.message}
        </Text>
      )}

      {isLoading && rows.length === 0 ? (
        <Center mih={200}>
          <Loader size="md" />
        </Center>
      ) : rows.length === 0 ? (
        <Center mih={200}>
          <Text c="dimmed">No entities match.</Text>
        </Center>
      ) : mode === 'cases' ? (
        <CasesTable
          cases={cases}
          teamName={teamName}
          onNavigate={(row) => navigate(routeForRow(row))}
          onCodenameRenamed={onCodenameRenamed}
        />
      ) : (
        <AllItemsTable
          rows={rows}
          teamName={teamName}
          onNavigate={(row) => navigate(routeForRow(row))}
          onCodenameRenamed={onCodenameRenamed}
        />
      )}

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination
            value={clampedPage}
            onChange={onPageChange}
            total={totalPages}
            size="sm"
            data-testid="browse-pagination"
          />
        </Group>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Cases mode — one row per codename family
// ---------------------------------------------------------------------------

function CasesTable({
  cases,
  teamName,
  onNavigate,
  onCodenameRenamed,
}: Readonly<{
  cases: CaseGroup[];
  teamName: (teamId: string | null) => string | null;
  onNavigate: (row: BrowseEntityRow) => void;
  onCodenameRenamed?: () => void | Promise<void>;
}>) {
  return (
    <Table.ScrollContainer minWidth={600}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Case</Table.Th>
            <Table.Th>Contents</Table.Th>
            <Table.Th>Client</Table.Th>
            <Table.Th>Latest</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {cases.map((c) => (
            <Table.Tr key={c.key} data-testid={`browse-case-${c.key}`}>
              <Table.Td>
                {c.family ? (
                  <EditableCodename
                    value={c.family}
                    size="sm"
                    fw={500}
                    onSaved={onCodenameRenamed}
                    onStaleKey={onCodenameRenamed}
                  />
                ) : (
                  <Text size="sm" fw={500}>
                    {c.members[0]?.codename ?? '—'}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="wrap">
                  {c.members.map((m) => {
                    const pill = pillForKind(m.kind);
                    return (
                      // Tooltip backstop: a squeezed badge ellipsises its
                      // label with no other way to read the stage.
                      <Tooltip
                        key={`${m.kind}-${m.id}`}
                        label={`${pill.label} · ${m.stageLabel}`}
                        withArrow
                      >
                        <Badge
                          color={pill.color}
                          variant="light"
                          size="sm"
                          component="button"
                          type="button"
                          style={{ cursor: 'pointer' }}
                          onClick={() => onNavigate(m)}
                          data-testid={`browse-member-${m.id}`}
                        >
                          {pill.label} · {m.stageLabel}
                        </Badge>
                      </Tooltip>
                    );
                  })}
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {teamName(c.teamId) ?? '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {c.latest ? timeAgo(c.latest) : '—'}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

// ---------------------------------------------------------------------------
// All-items mode — the flat entity table
// ---------------------------------------------------------------------------

function AllItemsTable({
  rows,
  teamName,
  onNavigate,
  onCodenameRenamed,
}: Readonly<{
  rows: BrowseEntityRow[];
  teamName: (teamId: string | null) => string | null;
  onNavigate: (row: BrowseEntityRow) => void;
  onCodenameRenamed?: () => void | Promise<void>;
}>) {
  return (
    <Table.ScrollContainer minWidth={600}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Codename</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Stage / Lifecycle</Table.Th>
            <Table.Th>Client</Table.Th>
            <Table.Th>Created</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const pill = pillForKind(row.kind);
            return (
              <Table.Tr
                key={`${row.kind}-${row.id}`}
                onClick={() => onNavigate(row)}
                style={{ cursor: 'pointer' }}
                data-testid={`browse-row-${row.id}`}
              >
                <Table.Td>
                  {row.codename && row.codenameFamily ? (
                    <EditableCodename
                      value={row.codenameFamily}
                      suffix={
                        row.codename.startsWith(row.codenameFamily)
                          ? row.codename.slice(row.codenameFamily.length) || null
                          : null
                      }
                      size="sm"
                      fw={500}
                      onSaved={onCodenameRenamed}
                      onStaleKey={onCodenameRenamed}
                      data-testid={`browse-codename-${row.id}`}
                    />
                  ) : (
                    <Text size="sm" fw={500}>
                      {row.codename ?? (
                        <Text component="span" c="dimmed">
                          —
                        </Text>
                      )}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Badge color={pill.color} variant="light" size="sm">
                    {pill.label}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{row.stageLabel}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {teamName(row.teamId) ?? '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {timeAgo(row.createdAt)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
